<template>
  <div class="d-flex flex-column bg-white" style="height: 100%; width: 100%; position: relative;">
      <!-- Builder Body -->
      <div class="d-flex flex-grow-1 border-bottom" style="overflow: hidden;">
          <!-- Canvas Area -->
          <section class='invoice-builder-container invoice-container flex-grow-1 h-100 overflow-auto bg-light' id="builder-canvas-area" style="position: relative;" @scroll="updateAppearanceToolbarPositionFor(selectedElement)">
              <div v-show="!developerMode" class="invoice-builder" id="invoice-container" style="margin-top: 1rem;" @click="handleClick" data-invoicly="true" data-only-settings="true"></div>
              
              <!-- Developer Mode JSON Editor -->
              <div v-if="developerMode" style="height: 100%; min-height: 500px; padding: 20px; background: white; border: 1px solid #ddd; display: flex; flex-direction: column;">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                      <h5 class="mb-0">JSON Template Editor</h5>
                      <button class="btn btn-sm btn-outline-primary" @click="copyJsonTemplate">
                          <AppIcon name="bi-clipboard" class="bi bi-clipboard" /> Copy
                      </button>
                  </div>
                  <textarea v-model="jsonTemplate" spellcheck="false" class="form-control" style="flex: 1; font-family: monospace; font-size: 13px; white-space: pre; resize: none;"></textarea>
              </div>
          </section>


          <!-- Appearance Toolbar (top: color / font / align / border / layout) -->
          <transition name="toolbar-fade">
            <div
              v-if="appearanceToolbarVisible && selectedElement && (selectedElement.tagName === 'IMG' || !selectedElement.getAttribute('data-only-settings'))"
              class="iab-top-toolbar"
              :style="appearanceToolbarStyle"
              @mousedown.stop
            >
              <!-- TOP ROW: Typography, Colors, Borders -->
              <div class="iab-top-toolbar-row">
                <!-- Text Color -->
                <div class="iab-top-group" title="Text Color">
                  <label class="iab-top-color-wrap">
                    <AppIcon name="bi-fonts" class="bi bi-fonts iab-top-icon" />
                    <input type="color" v-model="style.color" @change="colorChanged" class="iab-top-color-input">
                    <span class="iab-top-color-bar" :style="{background: style.color}"></span>
                  </label>
                </div>
                <!-- Background Color -->
                <div class="iab-top-group" title="Background Color">
                  <label class="iab-top-color-wrap">
                    <AppIcon name="bi-paint-bucket" class="bi bi-paint-bucket iab-top-icon" />
                    <input type="color" v-model="style.backgroundColor" @change="backgroundColorChanged" class="iab-top-color-input">
                    <span class="iab-top-color-bar" :style="{background: style.backgroundColor}"></span>
                  </label>
                </div>

                <div class="iab-top-sep"></div>

                <!-- Font Size -->
                <div class="iab-top-group iab-top-fontsize">
                  <input
                    type="number"
                    v-model="style.fontSize"
                    @change="fontSizeChanged"
                    class="iab-top-size-input"
                    title="Font Size (px)"
                    min="6" max="120"
                  >
                  <span class="iab-top-size-unit">px</span>
                </div>
                <!-- Bold -->
                <button class="iab-top-btn" :class="{active: style.fontWeight === 'bolder'}" @click.stop="setFontWeight('bolder')" title="Bold"><AppIcon name="bi-type-bold" class="bi bi-type-bold" /></button>
                <!-- Light -->
                <button class="iab-top-btn" :class="{active: style.fontWeight === 'lighter'}" @click.stop="setFontWeight('lighter')" title="Light"><AppIcon name="bi-type" class="bi bi-type" /></button>

                <div class="iab-top-sep"></div>

                <!-- Alignment -->
                <button class="iab-top-btn" :class="{active: style.textAlign === 'left'}"   @click.stop="setAlign('left')"   title="Align Left"><AppIcon name="bi-text-left" class="bi bi-text-left" /></button>
                <button class="iab-top-btn" :class="{active: style.textAlign === 'center'}" @click.stop="setAlign('center')" title="Align Center"><AppIcon name="bi-text-center" class="bi bi-text-center" /></button>
                <button class="iab-top-btn" :class="{active: style.textAlign === 'right'}"  @click.stop="setAlign('right')"  title="Align Right"><AppIcon name="bi-text-right" class="bi bi-text-right" /></button>

                <div class="iab-top-sep"></div>

                <!-- Borders -->
                <button class="iab-top-btn" @click.stop="setBorder('none')"   title="No Border"><AppIcon name="bi-slash-circle" class="bi bi-slash-circle" /></button>
                <button class="iab-top-btn" @click.stop="setBorder('all')"    title="All Borders"><AppIcon name="bi-square" class="bi bi-square" /></button>
                <button class="iab-top-btn" @click.stop="setBorder('bottom')" title="Bottom Border"><AppIcon name="bi-border-bottom" class="bi bi-border-bottom" /></button>

                <!-- Image URL Edit (Only for IMG) -->
                <div v-if="selectedElement && selectedElement.tagName === 'IMG'" class="iab-top-sep"></div>
                <div v-if="selectedElement && selectedElement.tagName === 'IMG'" class="iab-top-group" style="flex: 1; min-width: 150px;">
                  <input 
                    type="text" 
                    v-model="style.imageSrc" 
                    @input="updateSelectedImageSrc"
                    class="iab-top-size-input w-100" 
                    style="text-align: left; padding: 0 8px;"
                    placeholder="Image URL..."
                  >
                </div>
              </div>

              <!-- BOTTOM ROW: Positioning, Layout, Spacing, Actions -->
              <div class="iab-top-toolbar-row">
                <!-- Layout: Width & Height -->
                <div class="iab-top-group iab-top-fontsize" title="Width">
                  <span class="iab-top-size-unit" style="margin-right:2px;">W</span>
                  <input type="number" v-model="style.width" @change="setSize('width')" class="iab-top-size-input iab-top-size-input--wide" placeholder="auto">
                  <span class="iab-top-size-unit">px</span>
                </div>
                <div class="iab-top-group iab-top-fontsize" title="Height" style="margin-left:4px;">
                  <span class="iab-top-size-unit" style="margin-right:2px;">H</span>
                  <input type="number" v-model="style.height" @change="setSize('height')" class="iab-top-size-input iab-top-size-input--wide" placeholder="auto">
                  <span class="iab-top-size-unit">px</span>
                </div>

                <div class="iab-top-sep"></div>

                <!-- Spacing toggle -->
                <button
                  class="iab-top-btn"
                  :class="{active: spacingPanelVisible}"
                  @click.stop="spacingPanelVisible = !spacingPanelVisible; addPanelVisible = false"
                  title="Padding & Margin"
                >
                  <AppIcon name="bi-bounding-box" class="bi bi-bounding-box" />
                </button>

                <div class="iab-top-sep"></div>

                <!-- Display On (Floating Toolbar) -->
                <div class="d-flex align-items-center" style="gap: 2px;">
                  <div class="d-flex" style="gap: 2px;">
                    <span v-for="(val, idx) in displayValues.slice(0, 2)" :key="'toolbar-dsp-' + val" class="badge bg-light text-dark border d-flex align-items-center" style="font-size:9px; padding: 2px 4px;">
                      {{ $t(val) }}
                      <AppIcon name="bi-x" class="bi bi-x ms-1" style="cursor:pointer;" @click.stop="removeDisplayValue(idx)" />
                    </span>
                    <span v-if="displayValues.length > 2" class="badge bg-light text-dark border" style="font-size:9px; padding: 2px 4px;">...</span>
                  </div>
                  <select v-model="newDisplayValue" @change="addDisplayValue" class="iab-top-size-input ms-1" :disabled="displayValues[0] === 'always'" style="width: 85px; text-align: left; padding: 0 4px;" title="Display On">
                    <option disabled value="">Show on...</option>
                    <option v-for="option in ['always','invoice','receipt','order','purchase','delivery','reception','return','quote']" :key="'tb-opt-' + option" :value="option" :disabled="displayValues.includes(option)">{{ $t(option) }}</option>
                  </select>
                </div>

                <div class="iab-top-sep"></div>

                <!-- Actions: Add / Move Up / Move Down / Delete -->
                <button class="iab-top-btn" :class="{active: addPanelVisible}" @click.stop="addPanelVisible = !addPanelVisible; spacingPanelVisible = false" title="Add Element Below">
                  <AppIcon name="bi-plus-lg" class="bi bi-plus-lg" />
                </button>
                <button class="iab-top-btn iab-top-btn--move" @click.stop="moveElement('up')" title="Move Up">
                  <AppIcon name="bi-chevron-up" class="bi bi-chevron-up" />
                </button>
                <button class="iab-top-btn iab-top-btn--move" @click.stop="moveElement('down')" title="Move Down">
                  <AppIcon name="bi-chevron-down" class="bi bi-chevron-down" />
                </button>
                <button class="iab-top-btn iab-top-btn--delete" @click.stop="deleteElement" title="Delete Element">
                  <AppIcon name="bi-trash" class="bi bi-trash" />
                </button>
              </div>
              
              <!-- Add Element sub-panel (inline below the toolbar row) -->
              <div v-if="addPanelVisible" class="iab-spacing-panel iab-add-panel">
                <!-- Step 1: Select Type -->
                <template v-if="!selectedAddType">
                  <div class="iab-add-item" @click.stop="selectedAddType = 'text'">
                    <AppIcon name="bi-fonts" class="bi bi-fonts" style="font-size: 16px; color: #0d6efd;" />
                    <span>Text</span>
                  </div>
                  <div class="iab-add-sep"></div>
                  <div class="iab-add-item" @click.stop="selectedAddType = 'image'">
                    <AppIcon name="bi-image" class="bi bi-image" style="font-size: 16px; color: #0d6efd;" />
                    <span>Image</span>
                  </div>
                </template>

                <!-- Step 2: Select Width (and URL for images) -->
                <template v-else>
                  <div class="d-flex flex-column gap-2 w-100">
                    <div class="d-flex align-items-center gap-2">
                       <button class="iab-top-btn" @click.stop="selectedAddType = null" title="Back">
                         <AppIcon name="bi-arrow-left" class="bi bi-arrow-left" />
                       </button>
                       <span style="font-size: 11px; font-weight: bold; color: #666;">Adding {{ selectedAddType }}</span>
                    </div>

                    <div v-if="selectedAddType === 'image'" class="iab-add-url-row px-1">
                      <input 
                        type="text" 
                        v-model="newImageSrc" 
                        class="iab-top-size-input w-100" 
                        placeholder="Image URL (http://...)"
                        @click.stop
                      >
                    </div>

                    <div class="d-flex align-items-center justify-content-center gap-2">
                      <div class="iab-add-item" @click.stop="addElement('12')">
                        <div class="iab-add-preview"><div class="iab-add-bar" style="width: 100%;"></div></div>
                        <span>1/1</span>
                      </div>
                      <div class="iab-add-sep"></div>
                      <div class="iab-add-item" @click.stop="addElement('6')">
                        <div class="iab-add-preview"><div class="iab-add-bar" style="width: 50%;"></div></div>
                        <span>1/2</span>
                      </div>
                      <div class="iab-add-sep"></div>
                      <div class="iab-add-item" @click.stop="addElement('4')">
                        <div class="iab-add-preview"><div class="iab-add-bar" style="width: 33%;"></div></div>
                        <span>1/3</span>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <!-- Spacing sub-panel (inline below the toolbar row) -->
              <div v-if="spacingPanelVisible" class="iab-spacing-panel">
                <!-- Padding -->
                <div class="iab-spacing-col">
                  <div class="iab-spacing-label">Padding</div>
                  <div class="iab-spacing-grid">
                    <input type="number" v-model="style.paddingTop"    @change="setPadding('top')"    class="iab-sp-input iab-sp-top"    placeholder="T">
                    <input type="number" v-model="style.paddingRight"  @change="setPadding('right')"  class="iab-sp-input iab-sp-right"  placeholder="R">
                    <input type="number" v-model="style.paddingBottom" @change="setPadding('bottom')" class="iab-sp-input iab-sp-bottom" placeholder="B">
                    <input type="number" v-model="style.paddingLeft"   @change="setPadding('left')"   class="iab-sp-input iab-sp-left"   placeholder="L">
                    <div class="iab-sp-center"><AppIcon name="bi-aspect-ratio" class="bi bi-aspect-ratio" /></div>
                  </div>
                </div>
                <div class="iab-spacing-divider"></div>
                <!-- Margin -->
                <div class="iab-spacing-col">
                  <div class="iab-spacing-label">Margin</div>
                  <div class="iab-spacing-grid iab-spacing-grid--margin">
                    <input type="number" v-model="style.marginTop"    @change="setMargin('top')"    class="iab-sp-input iab-sp-top"    placeholder="T">
                    <input type="number" v-model="style.marginRight"  @change="setMargin('right')"  class="iab-sp-input iab-sp-right"  placeholder="R">
                    <input type="number" v-model="style.marginBottom" @change="setMargin('bottom')" class="iab-sp-input iab-sp-bottom" placeholder="B">
                    <input type="number" v-model="style.marginLeft"   @change="setMargin('left')"   class="iab-sp-input iab-sp-left"   placeholder="L">
                    <div class="iab-sp-center"><AppIcon name="bi-arrows-expand" class="bi bi-arrows-expand" /></div>
                  </div>
                </div>
              </div>
            </div>
          </transition>

          <!-- Settings Sidebar Removed (Everything is handled via inline toolbars) -->
      </div>

      <!-- Full-Width Sticky Action Footer -->
      <div class="settings-footer-full p-2 bg-white border-top shadow-lg" style="position: sticky; bottom: 0; z-index: 1050;">
          <div class="container-fluid px-2">
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <!-- Left Side -->
                  <div class="d-flex align-items-center gap-2">
                      <!-- View Document Mode -->
                      <!-- <div class="d-flex align-items-center gap-2">
                          <label class="text-muted fw-bold mb-0" style="font-size: 12px; white-space: nowrap;">
                              <AppIcon name="bi-filter-square" class="bi bi-filter-square" /> View Mode
                          </label>
                          <select v-model="filterType" class="form-select form-select-sm shadow-sm border-0 bg-light" @change="applyViewFilter" style="width: auto; min-width: 150px; font-weight: 600;">
                              <option value="all">Show All (Edit Mode)</option>
                              <option value="quote">{{$t('quote')}} View</option>
                              <option value="invoice">{{$t('invoice')}} View</option>
                              <option value="order">{{$t('order')}} View</option>
                              <option value="purchase">{{$t('purchase')}} View</option>
                              <option value="delivery">{{$t('delivery')}} View</option>
                              <option value="reception">{{$t('reception')}} View</option>
                              <option value="return">{{$t('return')}} View</option>
                          </select>
                      </div> -->

                      <!-- <div style="width: 1px; height: 24px; background: #dee2e6;"></div> -->

                      <!-- Developer Mode Toggle -->
                      <div class="form-check form-switch d-flex align-items-center mb-0" title="Developer JSON Editor">
                          <input class="form-check-input me-2 mt-0" type="checkbox" id="devModeToggle" v-model="developerMode" @change="onDeveloperModeChange" style="width: 38px; height: 20px; cursor: pointer;">
                          <label class="form-check-label text-muted fw-bold" for="devModeToggle" style="font-size: 13px; cursor: pointer;"><AppIcon name="bi-braces" class="bi bi-braces" /> Developer Mode</label>
                      </div>

                      <!-- Template Switcher Dropdown -->
                      <!-- <div class="ms-4 border-start ps-4">
                          <select v-model="selectedTemplateKey" @change="switchTemplate" class="form-select form-select-sm shadow-sm border-0 bg-light" style="width: auto; font-weight: 600; cursor: pointer;">
                              <option value="default">Default Template</option>
                              <option value="minimal">Minimalist Design</option>
                              <option value="modern">Modern Design</option>
                              <option value="elegant">Elegant Style</option>
                              <option value="bold">Bold Style</option>
                              <option value="clean">Clean Style</option>
                              <option value="executive">Executive Style</option>
                              <option value="teal">Teal Professional</option>
                              <option value="doli">Doli</option>
                              <option value="autoEntrepreneur">{{$t('auto_entrepreneur_template')}}</option>
                              <option value="corporateNavy">Corporate Navy</option>
                              <option value="financeGreen">Finance Green</option>
                              <option value="burgundyClassic">Burgundy Classic</option>
                              <option value="sandstone">Sandstone Studio</option>
                          </select>
                      </div> -->

                  </div>

                  <!-- Right Side -->
                  <div class="d-flex align-items-center gap-2">
                      <button class="btn btn-outline-secondary rounded-pill shadow-sm px-4 py-2" @click="resetTemplate">
                          <AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise" /> <span class="d-none d-md-inline ms-1">{{$t('reset')}}</span>
                      </button>
                      
                      <button class="btn btn-outline-danger rounded-pill shadow-sm px-4 py-2" v-if="onboarding === undefined && hasChanges" :disabled="loading" @click="discardChanges">
                          <AppIcon name="bi-x-circle" class="bi bi-x-circle" /> <span class="d-none d-md-inline ms-1">{{$t('discard') || 'Discard'}}</span>
                      </button>

                      <div class="dropdown">
                          <button class="btn btn-primary rounded-pill shadow-sm px-4 py-2 dropdown-toggle" type="button" id="previewDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                              <AppIcon name="bi-eye" class="bi bi-eye" /> <span class="d-none d-md-inline ms-1">{{$t('preview')}}</span>
                          </button>
                          <ul class="dropdown-menu dropdown-menu-end shadow border-0" aria-labelledby="previewDropdown" style="border-radius: 12px; margin-bottom: 8px;">
                              <li><button class="dropdown-item py-2" @click="previewData('quote')"><AppIcon name="bi-file-text" class="bi bi-file-text me-2" />{{$t('quote')}}</button></li>
                              <li><button class="dropdown-item py-2" @click="previewData('invoice')"><AppIcon name="bi-receipt" class="bi bi-receipt me-2" />{{$t('invoice')}}</button></li>
                              <li><button class="dropdown-item py-2" @click="previewData('receipt')"><AppIcon name="bi-file-earmark-check" class="bi bi-file-earmark-check me-2" />{{$t('processes')}}</button></li>
                              <li><button class="dropdown-item py-2" @click="previewData('order')"><AppIcon name="bi-cart" class="bi bi-cart me-2" />{{$t('order')}}</button></li>
                              <li class="dropdown-divider"></li>
                              <li><button class="dropdown-item py-2" @click="previewData('purchase')"><AppIcon name="bi-bag-check" class="bi bi-bag-check me-2" />{{$t('purchase')}}</button></li>
                              <li><button class="dropdown-item py-2" @click="previewData('delivery')"><AppIcon name="bi-truck" class="bi bi-truck me-2" />{{$t('delivery')}}</button></li>
                              <li class="dropdown-divider"></li>
                              <li><button class="dropdown-item py-2" @click="previewData('reception')"><AppIcon name="bi-box-seam" class="bi bi-box-seam me-2" />{{$t('reception')}}</button></li>
                              <li><button class="dropdown-item py-2" @click="previewData('return')"><AppIcon name="bi-arrow-left-right" class="bi bi-arrow-left-right me-2" />{{$t('return')}}</button></li>
                          </ul>
                      </div>

                      <button class="btn btn-success rounded-pill shadow-sm px-5 py-2 fw-bold" v-if="onboarding === undefined" :disabled="loading" @click="saveTemplate">
                          <AppIcon name="bi-check-circle" class="bi bi-check-circle" /> <span class="d-none d-md-inline ms-1">{{$t('save')}}</span>
                      </button>
                  </div>
              </div>
          </div>
      </div>

      <!-- Modals -->
      <invoice-template-modal 
          v-if="invoicePreviewModalisOpen" 
          @modalClosed="invoicePreviewModalisOpen = false" 
          modalId="invoice-quote-modal-01" 
          :isModalOpen="invoicePreviewModalisOpen"  
          :custom-template="currentHtmlForPreview"
          :business-override="businessOverride"
          :type="previewType">
      </invoice-template-modal>

  </div>
</template>

<script>
import helper from '../helper';
import defaultTemplate from '../defaultTemplate.json'
import v2Template from '../v2Template.json'
import minimalTemplate from '../template_minimal.json'
import modernTemplate from '../template_modern.json'
import elegantTemplate from '../template_elegant.json'
import boldTemplate from '../template_bold.json'
import cleanTemplate from '../template_clean.json'
import executiveTemplate from '../template_executive.json'
import tealTemplate from '../template_teal.json'
import doliTemplate from '../template_doli.json'
import professionalInvoicePresets from '../invoiceProfessionalPresets'
import autoEntrepreneurTemplate from '../invoiceAutoEntrepreneurTemplate'
import InvoiceTemplateModal from '../components/invoiceLayout/InvoiceTemplateModal.vue';

export default {
  name: 'InvoiceBuilder',
  props : ['template','template_id','onboarding','starterTemplate','starterTemplateKey','businessOverride','adminAccountId'],
  components: {
    InvoiceTemplateModal
  },
  data(){
    return {
      loading : false,
      style : {
        backgroundColor: '#ffffff',
        color: '#000000',
        fontSize: '16',
        textAlign : 'center',
        fontWeight : 'lighter',
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        marginTop: 0,
        marginRight: 0,
        marginBottom: 0,
        marginLeft: 0,
        width: '',
        height: '',
        minHeight: '',
        imageSrc: ''
      },
      showSettings : false,
      selectedElement : null,
      currentValue : null,
      displayValues: [],
      newDisplayValue: '',
      selectedVariable: '',
      selectedGridSize: '',
      invoicePreviewModalisOpen : false,
      currentHtmlForPreview: null,
      developerMode: false,
      jsonTemplate: '',
      initialTemplateData: null,
      hasChanges: false,
      previewType: 'quote',
      filterType: 'all',
      selectedTemplateKey: this.starterTemplateKey || 'default',
      // Appearance toolbar (top, unified)
      appearanceToolbarVisible: false,
      appearanceToolbarStyle: {},
      // Spacing sub-panel toggle
      spacingPanelVisible: false,
      // Add Element panel toggle
      addPanelVisible: false,
      selectedAddType: null,
      newImageSrc: ''
    }
  },
  methods: {
    async copyJsonTemplate() {
      try {
        await navigator.clipboard.writeText(this.jsonTemplate);
        if (this.$toast) {
          this.$toast.success('JSON Template copied to clipboard');
        } else {
          this.$swal({ icon: 'success', text: 'JSON Template copied to clipboard', toast: true, position: 'bottom', showConfirmButton: false, timer: 3000 });
        }
      } catch (err) {
        // error copying text
      }
    },
    onDeveloperModeChange() {
      if (this.developerMode) {
        const currentHtml = document.querySelector('.invoice-builder').innerHTML;
        this.jsonTemplate = JSON.stringify(this.htmlToJsonTree(currentHtml), null, 2);
      } else {
        try {
          const parsed = JSON.parse(this.jsonTemplate);
          let html = helper.jsonToHtml(parsed);
          
          // Replace placeholders before setting innerHTML for "right away" rendering
          html = html.replace(/{business\.logo}/g, this.business?.logo || '');
          html = html.replace(/{business\.electronic_stamp}/g, this.business?.electronic_stamp || '');
          
          document.querySelector('.invoice-builder').innerHTML = html;
          this.emitUpdatedTemplate();
        } catch (e) {
          if (this.$toast) {
            this.$toast.error('Invalid JSON template: ' + e.message);
          } else {
            this.$swal({ icon: 'error', text: 'Invalid JSON template: ' + e.message });
          }
          this.developerMode = true; // prevent closing
        }
      }
    },
    previewData(type = 'quote'){
        this.previewType = type;
        this.currentHtmlForPreview = document.querySelector('.invoice-builder').innerHTML;
        this.invoicePreviewModalisOpen = true;
    },
    async switchTemplate() {
        const presets = {
          'default': defaultTemplate,
          'v2': v2Template,
          'minimal': minimalTemplate,
          'modern': modernTemplate,
          'elegant': elegantTemplate,
          'bold': boldTemplate,
          'clean': cleanTemplate,
          'executive': executiveTemplate,
          'teal': tealTemplate,
          'doli': doliTemplate,
          'autoEntrepreneur': autoEntrepreneurTemplate,
          ...professionalInvoicePresets
        };
        const selectedTemplate = presets[this.selectedTemplateKey];

        if (selectedTemplate) {
          let html = helper.jsonToHtml(selectedTemplate);
          
          // Replace placeholders
          html = html.replace(/{business\.logo}/g, this.business?.logo || '');
          html = html.replace(/{business\.electronic_stamp}/g, this.business?.electronic_stamp || '');
          
          const builder = document.querySelector('.invoice-builder');
          if (builder) {
            builder.innerHTML = html;
            this.selectedElement = null;
            this.showSettings = false;
            this.appearanceToolbarVisible = false;
            this.hasChanges = true;
            this.emitUpdatedTemplate();
            this.$nextTick(() => this.applyViewFilter());
          }
        }
    },
    handleClick(event) {
      const target = event.target;
      
      // If clicking an active input/textarea, don't change selection
      if (['TEXTAREA', 'INPUT'].includes(target.tagName)) return;

      // Find the nearest selectable element
      // We allow images to be selectable even if they have data-only-settings, so they can show the toolbar
      const selectable = target.closest('[data-invoicly]:not([data-only-settings]), img[data-invoicly]');

      if (selectable) {
        // Remove previous selection highlight from all elements
        document.querySelectorAll('.selected-builder-element').forEach(el => {
          el.classList.remove('selected-builder-element');
        });

        selectable.classList.add('selected-builder-element');
        this.selectedElement = selectable;
        
        // Initialize style properties
        const computedStyle = window.getComputedStyle(selectable);
        this.style.color = this.rgbaToHex(computedStyle.color);
        this.style.backgroundColor = this.rgbaToHex(computedStyle.backgroundColor);
        this.style.fontSize = computedStyle.fontSize.replace('px', '');
        this.style.textAlign = computedStyle.textAlign;

        // Advanced style properties
        this.style.paddingTop = parseInt(computedStyle.paddingTop) || 0;
        this.style.paddingRight = parseInt(computedStyle.paddingRight) || 0;
        this.style.paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
        this.style.paddingLeft = parseInt(computedStyle.paddingLeft) || 0;
        this.style.marginTop = parseInt(computedStyle.marginTop) || 0;
        this.style.marginRight = parseInt(computedStyle.marginRight) || 0;
        this.style.marginBottom = parseInt(computedStyle.marginBottom) || 0;
        this.style.marginLeft = parseInt(computedStyle.marginLeft) || 0;
        this.style.width = parseInt(computedStyle.width) || '';
        this.style.height = parseInt(computedStyle.height) || '';
        this.style.minHeight = parseInt(computedStyle.minHeight) || '';
        
        // Image source handling with placeholder detection
        if (selectable.tagName === 'IMG') {
          if (selectable.classList.contains('logo')) {
            this.style.imageSrc = '{business.logo}';
          } else if (selectable.classList.contains('electronic_stamp') || selectable.classList.contains('electronic-stamp')) {
            this.style.imageSrc = '{business.electronic_stamp}';
          } else {
            this.style.imageSrc = selectable.getAttribute('src') || selectable.src;
          }
        } else {
          this.style.imageSrc = '';
        }

        // Parse data-invoicly-display
        const displayAttr = selectable.getAttribute('data-invoicly-display');
        this.displayValues = displayAttr ? displayAttr.split(',').map(v => v.trim()).filter(Boolean) : [];
        this.newDisplayValue = '';

        this.selectedGridSize = '';
        if (selectable.className && typeof selectable.className === 'string') {
            const match = selectable.className.match(/\bcol(?:-(?:sm|md|lg|xl|xxl))?-\d+\b/);
            if (match) this.selectedGridSize = match[0];
        }

        this.showSettings = true;

        // Compute toolbar positions NOW while the element is still in the DOM.
        // We must do this synchronously BEFORE editTextField/editNumberField
        // replaces the element with a textarea (which would make getBoundingClientRect return 0,0).
        this.updateAppearanceToolbarPositionFor(selectable);
        
        if (selectable.tagName === 'IMG') {
          this.style.imageSrc = selectable.src;
        } else if (selectable.getAttribute('data-invoicly-type') === 'number') {
          this.editNumberField(selectable);
        } else {
          this.editTextField(selectable);
        }
      } else {
        const container = target.closest('[data-only-settings="true"]');
        if (container) {
            // Remove previous selection highlight when clicking the body/container
            document.querySelectorAll('.selected-builder-element').forEach(el => {
              el.classList.remove('selected-builder-element');
            });
            this.selectedElement = container;
            this.showSettings = true;

            // Initialize style properties for the container/body
            const computedStyle = window.getComputedStyle(container);
            this.style.color = this.rgbaToHex(computedStyle.color);
            this.style.backgroundColor = this.rgbaToHex(computedStyle.backgroundColor);
            this.style.fontSize = computedStyle.fontSize.replace('px', '');
            this.style.textAlign = computedStyle.textAlign;
            
            // Advanced style properties for body
            this.style.paddingTop = parseInt(computedStyle.paddingTop) || 0;
            this.style.paddingRight = parseInt(computedStyle.paddingRight) || 0;
            this.style.paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
            this.style.paddingLeft = parseInt(computedStyle.paddingLeft) || 0;
            this.style.marginTop = parseInt(computedStyle.marginTop) || 0;
            this.style.marginRight = parseInt(computedStyle.marginRight) || 0;
            this.style.marginBottom = parseInt(computedStyle.marginBottom) || 0;
            this.style.marginLeft = parseInt(computedStyle.marginLeft) || 0;
            this.style.width = parseInt(computedStyle.width) || '';
            this.style.height = parseInt(computedStyle.height) || '';
            this.style.minHeight = parseInt(computedStyle.minHeight) || '';
            
            this.displayValues = [];
            this.selectedGridSize = '';

            // Hide toolbar when clicking the body/container directly
            this.appearanceToolbarVisible = false;
            this.spacingPanelVisible = false;
        }
      }
      this.emitUpdatedTemplate();
    },
    updateAppearanceToolbarPositionFor(el) {
      if (!el) { this.appearanceToolbarVisible = false; return; }
      const elRect = el.getBoundingClientRect();
      if (!elRect.width && !elRect.height && !elRect.top && !elRect.left) {
        this.appearanceToolbarVisible = false;
        return;
      }
      // Center the toolbar horizontally over the element, place it above.
      // Toolbar is now split into two rows: width is roughly ~460px; height is approx 74px.
      const toolbarWidth = 460;
      const toolbarHeight = 74;
      const rawLeft = elRect.left + (elRect.width / 2) - (toolbarWidth / 2);
      const left = Math.max(8, Math.min(rawLeft, window.innerWidth - toolbarWidth - 8));
      const top  = elRect.top - toolbarHeight - 6;
      this.appearanceToolbarStyle = {
        position: 'fixed',
        top:  `${Math.max(top, 8)}px`,
        left: `${left}px`,
        zIndex: 99999,
      };
      this.appearanceToolbarVisible = true;
    },
    emitUpdatedTemplate() {
      const builder = document.querySelector('.invoice-builder');
      if (!builder) return;
      const updatedTemplate = JSON.stringify(this.htmlToJsonTree(builder.innerHTML));
      this.hasChanges = true;
      this.$emit('templateUpdated', updatedTemplate);
    },
    rgbaToHex(rgba) {
      if (!rgba.startsWith('rgb')) return rgba;
      const values = rgba.match(/\d+/g);
      const hex = values.slice(0, 3)
        .map(val => Number(val).toString(16).padStart(2, '0'))
        .join('');
      const alphaHex = values[3] !== undefined ? Math.round(parseFloat(values[3]) * 255).toString(16).padStart(2, '0') : '';
      return `#${hex}${alphaHex}`.toUpperCase();
    },
    editNumberField(editableDiv) {
      const inputElement = document.createElement('input');
      inputElement.type = 'number';
      inputElement.min = 0
      inputElement.style.width = editableDiv.offsetWidth * 1.8 + 'px';
      inputElement.value = editableDiv.innerText;
      inputElement.className = `${editableDiv.className} transparent-input text-right`.trim();

      editableDiv.replaceWith(inputElement);
      // Re-position the appearance toolbar on the live input field
      this.updateAppearanceToolbarPositionFor(inputElement);

      inputElement.addEventListener('blur', () => {
        editableDiv.innerText = inputElement.value;
        const rowId = editableDiv.getAttribute('data-invoicly-row-id')
        const field = editableDiv.getAttribute('data-invoicly-field')
        if(rowId && field){
          this.rows[rowId][field] = parseFloat(inputElement.value)
        }
        inputElement.replaceWith(editableDiv);
        this.currentValue = editableDiv.innerText
        this.emitUpdatedTemplate();
        this.$nextTick(() => this.updateAppearanceToolbarPositionFor(editableDiv));
      });

      inputElement.focus();
    },
    editTextField(editableDiv) {
      const inputElement = document.createElement('textarea');
      inputElement.style.height = editableDiv.offsetHeight + 'px';
      inputElement.style.backgroundColor = editableDiv.style.backgroundColor
      inputElement.style.fontSize = editableDiv.style.fontSize
      inputElement.style.textAlign = editableDiv.style.textAlign
      inputElement.style.color = editableDiv.style.color
      inputElement.style.width = '100%';
      inputElement.value = editableDiv.innerText;
      inputElement.className = `${editableDiv.className} transparent-input text-right`;

      editableDiv.replaceWith(inputElement);
      // Re-position the appearance toolbar on the live textarea
      this.updateAppearanceToolbarPositionFor(inputElement);

      inputElement.addEventListener('blur', () => {
        const rowId = editableDiv.getAttribute('data-invoicly-row-id')
        const field = editableDiv.getAttribute('data-invoicly-field')
        if(rowId && field){
          this.rows[rowId][field] = parseFloat(inputElement.value)
        }
        editableDiv.innerText = inputElement.value;
        inputElement.replaceWith(editableDiv);
        this.currentValue = editableDiv.innerText
        this.emitUpdatedTemplate();
        this.$nextTick(() => this.updateAppearanceToolbarPositionFor(editableDiv));
      });
      inputElement.focus();
    },
    fontSizeChanged(){
      this.selectedElement.style.fontSize = this.style.fontSize + 'px'
      this.emitUpdatedTemplate();
    },
    colorChanged(){
      this.selectedElement.style.color = this.style.color
      this.emitUpdatedTemplate();
    },
    backgroundColorChanged(){
      this.selectedElement.style.backgroundColor = this.style.backgroundColor
      this.emitUpdatedTemplate();
    },
    setAlign(where){
      this.style.textAlign = where
      this.selectedElement.style.textAlign = where
      this.emitUpdatedTemplate();
    },
    setBorder(position){
      if(position === 'all'){
        this.selectedElement.style.border = '1px solid black'
      } else if (position === 'bottom'){
        this.selectedElement.style.borderBottom = '1px solid black'
      } else {
        this.selectedElement.style.border = ''
        this.selectedElement.style.borderBottom = ''
      }
      this.emitUpdatedTemplate();
    },
    setFontWeight(where){
      this.style.fontWeight = where
      this.selectedElement.style.fontWeight = where
      this.emitUpdatedTemplate();
    },
    traverse(element) {
        const jsonNode = {};
        jsonNode.tag = element.tagName;
        jsonNode.attributes = {};
        for (let i = 0; i < element.attributes.length; i++) {
            const attr = element.attributes[i];
            if (!attr.name.startsWith('data-v-')) {
                if (attr.name === 'class') {
                    let cleanClass = attr.value.replace('selected-builder-element', '').trim();
                    cleanClass = cleanClass.replace('builder-filtered-out', '').trim();
                    if (cleanClass) jsonNode.attributes['class'] = cleanClass;
                } else if (attr.name === 'src' && jsonNode.tag === 'IMG') {
                    // Revert business assets to placeholders
                    if (element.classList.contains('logo')) {
                      jsonNode.attributes['src'] = '{business.logo}';
                    } else if (element.classList.contains('electronic-stamp') || element.classList.contains('electronic_stamp')) {
                      jsonNode.attributes['src'] = '{business.electronic_stamp}';
                    } else {
                      jsonNode.attributes[attr.name] = attr.value;
                    }
                } else {
                    jsonNode.attributes[attr.name] = attr.value;
                }
            }
        }
        jsonNode.children = [];
        for (let i = 0; i < element.childNodes.length; i++) {
            const child = element.childNodes[i];
            if (child.nodeType === Node.ELEMENT_NODE) {
                jsonNode.children.push(this.traverse(child));
            } else if (child.nodeType === Node.TEXT_NODE) {
                const textContent = child.textContent.trim();
                if (textContent !== '') jsonNode.children.push(textContent);
            }
        }
        return jsonNode;
    },
    htmlToJsonTree(htmlContent) {
        const domParser = new DOMParser();
        const document = domParser.parseFromString(htmlContent, 'text/html');
        return this.traverse(document.body);
    },
    async saveTemplate() {
      try{
            this.loading = true
            const data = JSON.stringify(this.htmlToJsonTree(document.querySelector('.invoice-builder').innerHTML));
            if(this.adminAccountId){
              const response = await helper.customPatch(
                `${process.env.VUE_APP_API}/admin/accounts/${this.adminAccountId}/invoice-template`,
                { data },
                { showToast:true }
              )
              this.template_id = response.data.id
            }else if(this.template_id){
              await helper.customPatch(`${process.env.VUE_APP_API}/templates/${this.template_id}`,{ data }, { showToast:true })
            }else{
              await helper.customPost(`${process.env.VUE_APP_API}/templates`,{ type : 'standard', data }, { showToast:true })
            }
            this.loading = false
            this.hasChanges = false
            this.$emit('templateSaved')
        }catch(error){
            this.loading = false
        }
    },
    async resetTemplate(){
      const result = await this.$swal.fire({
          title: this.$t('are_you_sure') || 'Are you sure?',
          text: "This will revert your template to the default design!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('yes') || 'Yes',
          cancelButtonText: this.$t('cancel') || 'Cancel'
      });
      
      if(result.isConfirmed) {
        try{
            this.loading = true
            if(this.adminAccountId){
              await helper.customDelete(
                `${process.env.VUE_APP_API}/admin/accounts/${this.adminAccountId}/invoice-template`,
                {showToast:false}
              )
            }else if(this.template_id){
              await helper.customDelete(`${process.env.VUE_APP_API}/templates/${this.template_id}`,{showToast:false})
            }
            this.template_id = null
            let html = helper.jsonToHtml(defaultTemplate);
            
            // Replace placeholders before setting innerHTML for "right away" rendering
            html = html.replace(/{business\.logo}/g, this.business?.logo || '');
            html = html.replace(/{business\.electronic_stamp}/g, this.business?.electronic_stamp || '');
            
            this.template = html;
            this.loading = false    
            
            const builder = document.querySelector('.invoice-builder');
            if (builder) {
                builder.innerHTML = this.template;
                
                this.initialTemplateData = builder.innerHTML;
                this.selectedElement = null;
                this.showSettings = false;
                this.$nextTick(() => {
                    this.applyViewFilter();
                    this.emitUpdatedTemplate();
                    this.hasChanges = false;
                });
            }
          }catch(error){
            this.loading = false
        }
      }
    },
    setPadding(side) {
      if (!this.selectedElement) return;
      const prop = `padding${side.charAt(0).toUpperCase() + side.slice(1)}`;
      this.selectedElement.style[prop] = `${this.style[prop]}px`;
      this.emitUpdatedTemplate();
    },
    setMargin(side) {
      if (!this.selectedElement) return;
      const prop = `margin${side.charAt(0).toUpperCase() + side.slice(1)}`;
      this.selectedElement.style[prop] = `${this.style[prop]}px`;
      this.emitUpdatedTemplate();
    },
    setSize(dimension) {
      if (!this.selectedElement) return;
      const value = this.style[dimension];
      this.selectedElement.style[dimension] = value ? `${value}px` : '';
      this.emitUpdatedTemplate();
    },
    insertVariable() {
      if (!this.selectedElement || !this.selectedVariable) return;
      this.selectedElement.innerText += this.selectedVariable;
      this.currentValue = this.selectedElement.innerText;
      this.selectedVariable = '';
      this.emitUpdatedTemplate();
    },
    insertElectronicStamp() {
      if (!this.selectedElement) return;
      const img = document.createElement('img');
      img.src = this.business.electronic_stamp || 'https://placehold.co/150x150?text=Stamp';
      img.className = 'electronic_stamp';
      img.style.width = '120px';
      img.style.marginTop = '10px';
      img.style.marginBottom = '10px';
      img.setAttribute('data-invoicly', 'true');
      img.setAttribute('data-invoicly-display', 'always');
      img.setAttribute('data-only-settings', 'true');
      this.selectedElement.insertAdjacentElement('afterend', img);
      this.emitUpdatedTemplate();
    },
    removeDisplayValue(idx) {
      this.displayValues.splice(idx, 1);
      this.updateDisplayAttribute();
    },
    addDisplayValue() {
      const val = this.newDisplayValue.trim();
      if (val && !this.displayValues.includes(val)) {
        this.displayValues.push(val);
        this.updateDisplayAttribute();
      }
      this.newDisplayValue = '';
    },
    updateDisplayAttribute() {
      if (this.selectedElement) {
        if (this.displayValues.length) {
          this.selectedElement.setAttribute('data-invoicly-display', this.displayValues.join(','));
        } else {
          this.selectedElement.removeAttribute('data-invoicly-display');
        }
        this.applyViewFilter();
        this.emitUpdatedTemplate();
      }
    },
    addNewElement(type) {
      let newEl;
      const commonAttrs = {
        'data-invoicly': 'true',
        'data-invoicly-display': 'always',
        'style': 'padding: 10px; margin-top: 5px; margin-bottom: 5px; position: relative; background-color: #ffffff;'
      };

      switch(type) {
        case 'text': {
          newEl = document.createElement('div');
          newEl.innerText = 'New Text Block';
          newEl.style.minHeight = '20px';
          break;
        }
        case 'image': {
          newEl = document.createElement('img');
          newEl.src = 'https://placehold.co/150x150?text=New+Image';
          newEl.className = 'img-fluid';
          newEl.style.maxWidth = '150px';
          newEl.style.display = 'block';
          newEl.setAttribute('data-only-settings', 'true');
          break;
        }
        case 'line': {
          newEl = document.createElement('div');
          newEl.style.borderBottom = '1px solid #ddd';
          newEl.style.width = '100%';
          newEl.style.height = '1px';
          newEl.style.padding = '0';
          newEl.style.marginTop = '15px';
          newEl.style.marginBottom = '15px';
          break;
        }
        case 'spacer': {
          newEl = document.createElement('div');
          newEl.style.height = '40px';
          newEl.style.padding = '0';
          break;
        }
        case 'row': {
          newEl = document.createElement('div');
          newEl.className = 'row g-3';
          newEl.style.marginTop = '10px';
          newEl.style.marginBottom = '10px';
          
          const col1 = document.createElement('div');
          col1.className = 'col-6';
          col1.setAttribute('data-invoicly', 'true');
          col1.style.minHeight = '50px';
          col1.style.padding = '10px';
          col1.style.border = '1px dashed #eee';
          col1.style.backgroundColor = '#ffffff';
          col1.innerText = 'Column 1';
          
          const col2 = document.createElement('div');
          col2.className = 'col-6';
          col2.setAttribute('data-invoicly', 'true');
          col2.style.minHeight = '50px';
          col2.style.padding = '10px';
          col2.style.border = '1px dashed #eee';
          col2.style.backgroundColor = '#ffffff';
          col2.innerText = 'Column 2';
          
          newEl.appendChild(col1);
          newEl.appendChild(col2);
          break;
        }
      }

      if (newEl) {
        // Apply common attributes
        for (const [key, value] of Object.entries(commonAttrs)) {
          if (key === 'style') {
            newEl.style.cssText += value;
          } else {
            newEl.setAttribute(key, value);
          }
        }

        const builder = document.querySelector('.invoice-builder');
        if (this.selectedElement && this.selectedElement !== builder) {
          this.selectedElement.insertAdjacentElement('afterend', newEl);
        } else {
          if (builder) builder.appendChild(newEl);
        }
        
        this.emitUpdatedTemplate();
        
        // Use $nextTick and a slight delay to ensure DOM is updated and then click it
        this.$nextTick(() => {
            setTimeout(() => {
                newEl.click();
            }, 50);
        });
      }
    },
    async addElement(width) {
      if (width && this.selectedAddType) {
        let newEl;
        if (this.selectedAddType === 'image') {
          newEl = document.createElement('img');
          let src = this.newImageSrc.trim();
          if (src === '{business.logo}') {
            newEl.src = this.business?.logo || '';
            newEl.classList.add('logo');
          } else if (src === '{business.electronic_stamp}') {
            newEl.src = this.business?.electronic_stamp || '';
            newEl.classList.add('electronic_stamp');
          } else {
            newEl.src = src || 'https://placehold.co/150x150?text=Logo';
          }
          newEl.style.maxWidth = '150px';
          newEl.style.display = 'block';
          newEl.setAttribute('data-only-settings', 'true');
        } else {
          newEl = document.createElement('div');
          newEl.innerText = 'New Text Block';
          newEl.style.minHeight = '30px';
        }

        newEl.className = `${this.selectedAddType === 'image' ? 'img-fluid ' : ''}col-${width}`;
        newEl.setAttribute('data-invoicly', 'true');
        newEl.setAttribute('data-invoicly-display', 'always');
        newEl.style.padding = '10px';
        newEl.style.marginTop = '5px';
        newEl.style.marginBottom = '5px';
        newEl.style.backgroundColor = '#ffffff';

        const builder = document.querySelector('.invoice-builder');
        if (this.selectedElement && this.selectedElement !== builder) {
          this.selectedElement.insertAdjacentElement('afterend', newEl);
        } else {
          if (builder) builder.appendChild(newEl);
        }

        this.addPanelVisible = false;
        this.selectedAddType = null;
        this.newImageSrc = '';
        this.emitUpdatedTemplate();
        
        // Use $nextTick and a slight delay to ensure DOM is updated and then click it
        this.$nextTick(() => {
            setTimeout(() => {
                newEl.click();
            }, 50);
        });
      }
    },
    updateSelectedImageSrc() {
      if (this.selectedElement && this.selectedElement.tagName === 'IMG') {
        const val = this.style.imageSrc.trim();
        if (val === '{business.logo}') {
          this.selectedElement.src = this.business?.logo || '';
          this.selectedElement.classList.add('logo');
        } else if (val === '{business.electronic_stamp}') {
          this.selectedElement.src = this.business?.electronic_stamp || '';
          this.selectedElement.classList.add('electronic_stamp');
        } else {
          this.selectedElement.src = val;
          this.selectedElement.classList.remove('logo');
          this.selectedElement.classList.remove('electronic_stamp');
          this.selectedElement.classList.remove('electronic-stamp');
        }
        this.emitUpdatedTemplate();
      }
    },
    updateImageSrc(val) {
      if (this.selectedElement && this.selectedElement.tagName === 'IMG') {
        this.selectedElement.src = val;
        this.emitUpdatedTemplate();
      }
    },
    addColumnToRow() {
      const row = this.selectedElement.classList.contains('row') ? this.selectedElement : this.selectedElement.closest('.row');
      if (row) {
        const col = document.createElement('div');
        col.className = 'col-4'; // default to col-4 for 3 cols if row is empty or already has cols
        col.setAttribute('data-invoicly', 'true');
        col.style.minHeight = '50px';
        col.style.padding = '10px';
        col.style.border = '1px dashed #eee';
        col.style.backgroundColor = '#ffffff';
        col.innerText = 'New Column';
        row.appendChild(col);
        this.emitUpdatedTemplate();
        this.$nextTick(() => {
            setTimeout(() => {
                col.click();
            }, 50);
        });
      }
    },
    moveElement(direction) {
      if (!this.selectedElement || !this.selectedElement.hasAttribute('data-invoicly')) return;
      const el = this.selectedElement;
      const parent = el.parentElement;
      if (!parent) return;

      if (direction === 'up') {
        const prev = el.previousElementSibling;
        if (prev) parent.insertBefore(el, prev);
      } else {
        const next = el.nextElementSibling;
        if (next) parent.insertBefore(next, el);
      }
      this.emitUpdatedTemplate();
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.$nextTick(() => {
        this.updateAppearanceToolbarPositionFor(el);
      });
    },
    deleteElement() {
      if (!this.selectedElement || !this.selectedElement.hasAttribute('data-invoicly')) return;
      if (confirm(this.$t('are_you_sure') || 'Are you sure you want to delete this element?')) {
        this.selectedElement.remove(); // actually remove from DOM
        this.selectedElement = null;
        this.showSettings = false;
        this.appearanceToolbarVisible = false;
        this.spacingPanelVisible = false;
        this.emitUpdatedTemplate();
      }
    },
    async discardChanges() {
      if (!this.initialTemplateData) return;
      const result = await this.$swal.fire({
          title: this.$t('are_you_sure') || 'Are you sure?',
          text: "Discard all unsaved changes?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('yes') || 'Yes',
          cancelButtonText: this.$t('cancel') || 'Cancel'
      });
      if (result.isConfirmed) {
        document.querySelector('.invoice-builder').innerHTML = this.initialTemplateData;
        this.selectedElement = null;
        this.showSettings = false;
        this.hasChanges = false;
        this.emitUpdatedTemplate();
        this.$nextTick(() => this.applyViewFilter());
      }
    },
    applyViewFilter() {
      const builder = document.querySelector('.invoice-builder');
      if (!builder) return;
      const elements = builder.querySelectorAll('*[data-invoicly]');
      elements.forEach(el => {
        if (el.getAttribute('data-only-settings') === 'true') {
          el.classList.remove('builder-filtered-out');
          return;
        }
        const displayAttr = el.getAttribute('data-invoicly-display') || 'always';
        const values = displayAttr.split(',').map(v => v.trim()).filter(Boolean);
        if (this.filterType === 'all' || values.includes('always') || values.includes(this.filterType)) {
          el.classList.remove('builder-filtered-out');
        } else {
          el.classList.add('builder-filtered-out');
        }
      });
      if (this.selectedElement && this.selectedElement.classList.contains('builder-filtered-out')) {
        this.selectedElement.classList.remove('selected-builder-element');
        this.selectedElement = null;
        this.showSettings = false;
      }
    },
  },
  async mounted() {
    if(this.onboarding === true) this.emitUpdatedTemplate();
    const endpoint = this.adminAccountId
      ? `${process.env.VUE_APP_API}/admin/accounts/${this.adminAccountId}/invoice-template`
      : `${process.env.VUE_APP_API}/templates-by-type?type=standard`
    const response = await helper.customGet(endpoint);
    if (response.data && response.data.id) {
      this.template_id = response.data.id;
    }

    if (this.starterTemplate) {
      this.template = helper.jsonToHtml(this.starterTemplate);
    } else if (response.data && response.data.data) {
      this.template = helper.jsonToHtml(JSON.parse(response.data.data));
    } else {
      this.template = helper.jsonToHtml(defaultTemplate);
    }

    if(this.template && document.querySelector('.invoice-builder')){
      let html = this.template;
      
      // Replace placeholders before setting innerHTML for "right away" rendering
      html = html.replace(/{business\.logo}/g, this.business?.logo || '');
      html = html.replace(/{business\.electronic_stamp}/g, this.business?.electronic_stamp || '');
      
      document.querySelector('.invoice-builder').innerHTML = html;
      this.initialTemplateData = document.querySelector('.invoice-builder').innerHTML;
      this.$nextTick(() => this.applyViewFilter());
      this.hasChanges = false;
      this.showSettings = true;
    }

    // Update toolbar position on window resize
    this._resizeHandler = () => {
      if (this.selectedElement && !this.selectedElement.getAttribute('data-only-settings')) {
        this.updateAppearanceToolbarPositionFor(this.selectedElement);
      }
    };
    window.addEventListener('resize', this._resizeHandler);
  },
  beforeDestroy() {
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
  },
  computed : {
    business(){ return this.businessOverride || this.$store.getters.BUSINESS || {} }
  }
}
</script>

<style scoped>
  .invoice-builder {
    background: #fff;
    width: 100%;
    padding : 50px;
    box-shadow: 0 0 20px rgba(0,0,0,0.1);
  }
  
  table td { padding: 0.45rem !important; font-size: 15px; }
   
  .transparent-input {
    border: 1px dashed #0d6efd !important;
    background: transparent;
    outline: none;
  }
  
  .settings-div {
    background-color: white;
    padding: 0;
    overflow-y: auto;
  }

  .invoice-container { width: 100%; position : relative; }

  .settings-container {
    background: #f8f9fa;
    padding: 0;
    height: 100%;
    overflow: hidden;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .settings-container { display: none !important; }
    .invoice { width: 100% !important; padding: 10px !important; }
  }
  
  #invoice-container {
    transform: scale(0.9);
    transform-origin: top center;
    border: 1px solid #ddd;
    margin: 2rem auto;
    max-width: 800px;
  }

  .invoice-builder-container { background : #eee; height:100%; overflow-y: auto; }
</style>

<style>
/* Global UX Styles */
.settings-card { background: white; border: 1px solid #eee; border-radius: 8px; margin: 0 10px 15px 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
.settings-card-header { padding: 10px 15px; font-size: 11px; font-weight: 700; color: #666; text-transform: uppercase; border-bottom: 1px solid #f8f9fa; background: #fafafa; }
.settings-card-body { padding: 12px 15px; }
.settings-label { font-size: 10px; font-weight: 600; color: #999; margin-bottom: 5px; display: block; }
.custom-select { border-radius: 6px; font-size: 12px; }
.filter-badge { background: #e9ecef; color: #495057; font-size: 10px; padding: 3px 6px; }
.btn-icon-only { width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; padding: 0; border-radius: 6px; }

/* Color Picker */
.color-picker-wrapper { display: flex; align-items: center; gap: 8px; }
.form-control-color { width: 32px !important; height: 32px !important; padding: 2px !important; border: 1px solid #ddd; border-radius: 4px; }
.color-hex { font-size: 10px; font-family: monospace; color: #888; }

/* Spacing Grid */
.spacing-container { position: relative; width: 120px; height: 80px; margin: 0 auto; border: 1px dashed #ccc; border-radius: 4px; background: #fafafa; }
.spacing-container.margin { background: #fff9f0; border-color: #ffd8a8; }
.spacing-input { position: absolute; width: 32px; height: 20px; font-size: 10px; text-align: center; border: 1px solid #ddd; border-radius: 3px; padding: 0; }
.spacing-input.top { top: 4px; left: 50%; transform: translateX(-50%); }
.spacing-input.right { top: 50%; right: 4px; transform: translateY(-50%); }
.spacing-input.bottom { bottom: 4px; left: 50%; transform: translateX(-50%); }
.spacing-input.left { top: 50%; left: 4px; transform: translateY(-50%); }
.spacing-center { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: #ccc; font-size: 12px; }

/* Element Selection */
.selected-builder-element { outline: 2px solid #0d6efd !important; outline-offset: -2px; background-color: rgba(13, 110, 253, 0.03) !important; box-shadow: 0 0 10px rgba(13, 110, 253, 0.1) !important; z-index: 1000; }
.builder-filtered-out { display: none !important; }

/* Inline Action Toolbar */
.inline-action-toolbar {
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  z-index: 99999;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  padding: 5px 4px;
  box-shadow: 0 4px 16px rgba(13, 110, 253, 0.18), 0 1px 4px rgba(0,0,0,0.10);
  pointer-events: all;
  user-select: none;
}

.iab-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #495057;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, transform 0.12s;
  padding: 0;
}

.iab-btn:hover {
  background: #e9ecef;
  transform: scale(1.08);
}

.iab-btn.iab-btn-move:hover {
  background: #dbeafe;
  color: #0d6efd;
}

.iab-btn.iab-btn-delete:hover {
  background: #fee2e2;
  color: #dc3545;
}

.iab-divider {
  width: 18px;
  height: 1px;
  background: #dee2e6;
  margin: 1px 0;
}

/* Toolbar fade transition */
.toolbar-fade-enter-active, .toolbar-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.toolbar-fade-enter, .toolbar-fade-leave-to {
  opacity: 0;
  transform: scale(0.88);
}

/* ── Appearance Toolbar (top of element) ── */
.iab-top-toolbar {
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 8px;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  box-shadow: 0 4px 18px rgba(13,110,253,0.13), 0 1px 4px rgba(0,0,0,0.08);
  z-index: 99999;
  user-select: none;
  white-space: nowrap;
}

.iab-top-toolbar-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.iab-top-sep {
  width: 1px;
  height: 20px;
  background: #dee2e6;
  margin: 0 4px;
  flex-shrink: 0;
}

.iab-top-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #495057;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.14s, color 0.14s;
  padding: 0;
  flex-shrink: 0;
}
.iab-top-btn:hover { background: #e9ecef; color: #0d6efd; }
.iab-top-btn.active { background: #dbeafe; color: #0d6efd; }

/* Color swatch button */
.iab-top-group {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.iab-top-color-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  cursor: pointer;
  border-radius: 6px;
  overflow: hidden;
  margin: 0;
}
.iab-top-color-wrap:hover { background: #e9ecef; }
.iab-top-icon {
  font-size: 12px;
  color: #495057;
  pointer-events: none;
  line-height: 1;
}
.iab-top-color-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0; left: 0;
  cursor: pointer;
  padding: 0;
  border: none;
}
.iab-top-color-bar {
  display: block;
  width: 16px;
  height: 3px;
  border-radius: 2px;
  margin-top: 2px;
  pointer-events: none;
}

/* Font size inline input */
.iab-top-fontsize {
  display: flex;
  align-items: center;
  gap: 1px;
}
.iab-top-size-input {
  width: 36px;
  height: 24px;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  font-size: 11px;
  text-align: center;
  padding: 0 2px;
  color: #333;
  background: #f8f9fa;
}
.iab-top-size-input--wide { width: 44px; }
.iab-top-size-input:focus { outline: none; border-color: #0d6efd; background: #fff; }
.iab-top-size-unit {
  font-size: 9px;
  color: #aaa;
  margin-left: 1px;
}

/* ── Spacing Sub-panel ── */
.iab-spacing-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  padding: 10px 14px;
  box-shadow: 0 6px 20px rgba(13,110,253,0.13), 0 1px 4px rgba(0,0,0,0.08);
  z-index: 100000;
  white-space: nowrap;
}

.iab-spacing-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.iab-spacing-label {
  font-size: 9px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.iab-spacing-divider {
  width: 1px;
  height: 80px;
  background: #dee2e6;
  align-self: center;
}

/* The 4-directional grid box */
.iab-spacing-grid {
  position: relative;
  width: 100px;
  height: 68px;
  border: 1px dashed #ccc;
  border-radius: 5px;
  background: #fafafa;
}
.iab-spacing-grid--margin {
  background: #fff9f0;
  border-color: #ffd8a8;
}

.iab-sp-input {
  position: absolute;
  width: 30px;
  height: 18px;
  font-size: 9px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 3px;
  padding: 0;
  background: #fff;
  color: #333;
}
.iab-sp-input:focus { outline: none; border-color: #0d6efd; }
.iab-sp-top    { top: 3px;   left: 50%; transform: translateX(-50%); }
.iab-sp-right  { top: 50%;  right: 3px; transform: translateY(-50%); }
.iab-sp-bottom { bottom: 3px; left: 50%; transform: translateX(-50%); }
.iab-sp-left   { top: 50%;  left: 3px;  transform: translateY(-50%); }
.iab-sp-center {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  color: #ccc;
  font-size: 11px;
}

/* Add Element Panel specific */
.iab-add-panel {
  gap: 8px !important;
  padding: 8px 12px !important;
}
.iab-add-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 8px;
  transition: background 0.15s;
  min-width: 44px;
}
.iab-add-item:hover {
  background: #f8f9fa;
}
.iab-add-item span {
  font-size: 10px;
  font-weight: 600;
  color: #666;
}
.iab-add-preview {
  width: 24px;
  height: 12px;
  border: 1px solid #ddd;
  border-radius: 2px;
  background: #fff;
  padding: 1px;
}
.iab-add-bar {
  height: 100%;
  background: #0d6efd;
  border-radius: 1px;
}
.iab-add-sep {
  width: 1px;
  height: 24px;
  background: #eee;
  align-self: center;
}
.iab-add-url-row {
  width: 100%;
  margin-bottom: 4px;
}
.iab-add-url-row input {
  height: 30px !important;
  font-size: 12px !important;
  text-align: left !important;
  padding: 0 10px !important;
}

/* Actions Footer */
.settings-footer-full {
  background: #ffffff;
  border-top: 1px solid #eee;
  padding: 1rem 1.5rem !important;
  box-shadow: 0 -10px 25px rgba(0,0,0,0.05);
}

.settings-footer-full .btn {
  padding: 0.6rem 1rem;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.settings-footer-full .btn i {
  font-size: 1.1rem;
}

.settings-footer-full .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.settings-footer-full .btn-success {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border: none;
}

.settings-footer-full .btn-primary {
  background: linear-gradient(135deg, #0d6efd 0%, #00d2ff 100%);
  border: none;
}
#invoice-container .table-responsive {
    min-height: 400px;
}
</style>
